<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { brandNumber } from "@voltalis/frontend-utils";
import { useForm } from "vee-validate";
import { watch, ref } from "vue";
import { z } from "zod";

import { TDuplicateProgramCommand } from "@/composables/kili/internal/mutations/use-duplicate-program-mutation";
import { TSite } from "@/data/api/kili/sites/types/sites.type.ts";
import { SiteProgramOverview } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";

import InputText from "primevue/inputtext";
import Select from "primevue/select";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import BaseDialog from "@/components/ui/dialog/base-dialog.vue";

interface IProps {
  sites?: TSite[];
  duplicatedProgram?: SiteProgramOverview;
  isPending?: boolean;
}

interface IEmits {
  (e: "submit", name: TDuplicateProgramCommand): void;
}

const visible = defineModel<boolean>();
const props = withDefaults(defineProps<IProps>(), {
  isPending: false,
});
const emits = defineEmits<IEmits>();

const { t, k, rawT } = useTypedIntl();

const selectedSite = ref<TSite>();

const duplicateProgramFormSchema = z.object({
  name: z.string().min(1, "required"),
  siteId: z.number({ required_error: "required" }),
});

const { handleSubmit, errors, defineField, setFieldValue, meta } = useForm({
  validationSchema: toTypedSchema(duplicateProgramFormSchema),
  initialValues: {
    name: "",
    siteId: undefined,
  },
});

const [name] = defineField("name");

const submit = handleSubmit((values) => {
  if (props.isPending || !props.duplicatedProgram) return;

  const command: TDuplicateProgramCommand = {
    name: values.name,
    siteId: brandNumber(values.siteId),
    copiedSiteProgram: {
      siteId: props.duplicatedProgram.siteId,
      programId: props.duplicatedProgram.id,
      name: props.duplicatedProgram.name,
    },
  };

  emits("submit", command);
});

watch(
  () => props.duplicatedProgram,
  (newValues) => {
    if (!newValues) return;

    setFieldValue("name", t(k.global.misc.copyOf({ name: newValues.name })));
  },
  { immediate: true },
);

watch(selectedSite, (newValue) => {
  if (!newValue) return;

  setFieldValue("siteId", newValue.siteId);
});
</script>

<template>
  <BaseDialog
    v-model:visible="visible"
    modal
    size="small"
  >
    <template #header>
      <div class="flex flex-col">
        <h4 class="flex w-full font-semibold">
          <template v-if="props.duplicatedProgram">
            {{ t(k.control.programming.copyToOtherSite.title({ name: props.duplicatedProgram.name })) }}
          </template>
          <template v-else>
            {{ t(k.control.programming.copyToOtherSite.action) }}
          </template>
        </h4>
        <span class="text-text-secondary">{{ t(k.control.programming.copyToOtherSite.subtitle) }}</span>
      </div>
    </template>

    <form
      class="flex flex-col gap-6"
      @submit="submit"
    >
      <section class="flex flex-col gap-2">
        <label class="font-semibold text-text-secondary">{{ t(k.control.programming.creation.programName) }}</label>
        <InputText
          v-model="name"
          autofocus
          data-test-id="program-copy-name-input"
          :disabled="props.isPending"
          :invalid="!!errors.name"
        />
        <small
          v-if="errors.name"
          class="text-red-500"
        >
          {{ rawT(`veeValidate.messages.${errors.name}`) }}
        </small>
      </section>

      <section class="flex flex-col gap-2">
        <label class="font-semibold text-text-secondary">{{
          t(k.control.programming.copyToOtherSite.targetSite)
        }}</label>
        <Select
          v-model="selectedSite"
          class="w-full"
          data-test-id="program-copy-select-site"
          :disabled="props.isPending"
          :invalid="!!errors.siteId && meta.touched"
          option-label="name"
          :options="props.sites"
        />
        <small
          v-if="!!errors.siteId && meta.touched"
          class="text-red-500"
        >
          {{ rawT(`veeValidate.messages.${errors.siteId}`) }}
        </small>
      </section>
    </form>

    <template #footer>
      <div class="grid grid-flow-col grid-cols-2 gap-2 w-full">
        <HospitalityButton
          data-test-id="program-copy-cancel-button"
          :disabled="props.isPending"
          severity="secondary"
          @click="visible = false"
        >
          <i class="pi pi-arrow-left mr-2" />
          {{ t(k.global.misc.cancel) }}
        </HospitalityButton>
        <HospitalityButton
          data-test-id="program-copy-submit-button"
          :disabled="props.isPending"
          :label="t(k.global.misc.copy)"
          :loading="props.isPending"
          @click="submit"
        />
      </div>
    </template>
  </BaseDialog>
</template>
